import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import {
  TrendingUp,
  DollarSign,
  Palette,
  Calendar,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";

const ArtistPnlPage = () => {
  const { formatAmount } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedArtist, setExpandedArtist] = useState(null);
  const [artistDetails, setArtistDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Filters - default to current year
  const [monthFrom, setMonthFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-01`; // Start from January of current year
  });
  const [monthTo, setMonthTo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-12`; // End at December of current year
  });
  const [selectedArtist, setSelectedArtist] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedService, setSelectedService] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("month_from", monthFrom);
      params.append("month_to", monthTo);
      if (selectedArtist !== "all") params.append("artist_id", selectedArtist);
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (selectedService !== "all") params.append("service_type", selectedService);
      
      const res = await api.get(`/artist-pnl/analytics?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching artist PnL:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtistDetails = async (artistId) => {
    try {
      setLoadingDetails(true);
      const params = new URLSearchParams();
      params.append("month_from", monthFrom);
      params.append("month_to", monthTo);
      const res = await api.get(`/artist-pnl/details/${artistId}?${params.toString()}`);
      setArtistDetails(res.data);
    } catch (error) {
      console.error("Error fetching artist details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleExpandArtist = async (artistId) => {
    if (expandedArtist === artistId) {
      setExpandedArtist(null);
      setArtistDetails(null);
    } else {
      setExpandedArtist(artistId);
      await fetchArtistDetails(artistId);
    }
  };

  const handlePaymentStatusChange = async (detail, newStatus) => {
    try {
      const itemId = detail.id || detail.task_id;
      await api.put(`/artist-pnl/payment-status/${itemId}`, null, {
        params: { source: detail.source, new_status: newStatus }
      });
      toast.success(`Статус змінено на "${newStatus === 'paid' ? 'Оплачено' : newStatus === 'approved' ? 'Підтверджено' : 'Очікує'}"`);
      // Refresh details
      await fetchArtistDetails(expandedArtist);
      // Also refresh main data
      await fetchData();
    } catch (error) {
      toast.error("Помилка зміни статусу");
      console.error("Error changing status:", error);
    }
  };

  const applyFilters = () => {
    fetchData();
  };

  const resetFilters = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 5);
    setMonthFrom(d.toISOString().slice(0, 7));
    setMonthTo(new Date().toISOString().slice(0, 7));
    setSelectedArtist("all");
    setSelectedStatus("all");
    setSelectedService("all");
    setTimeout(fetchData, 100);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const formatMonthShort = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${monthNames[month - 1]} '${String(year).slice(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const totals = data?.overall_totals || { paid: 0, approved: 0, pending: 0, total: 0 };

  return (
    <div data-testid="artist-pnl-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Artist P&L</h1>
          <p className="text-sm text-zinc-500">Artist payments analysis by month</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Total</span>
              <DollarSign className="h-4 w-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-purple-400">{formatAmount(totals.total, "UAH")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Paid</span>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">{formatAmount(totals.paid, "UAH")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Approved</span>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-400">{formatAmount(totals.approved, "UAH")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Pending</span>
              <Clock className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">{formatAmount(totals.pending, "UAH")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium text-zinc-400 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-6 gap-4 items-end">
            {/* Month From */}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Month From</Label>
              <Input
                type="month"
                value={monthFrom}
                onChange={(e) => setMonthFrom(e.target.value)}
                className="bg-zinc-800 border-zinc-700 h-9"
              />
            </div>

            {/* Month To */}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Month To</Label>
              <Input
                type="month"
                value={monthTo}
                onChange={(e) => setMonthTo(e.target.value)}
                className="bg-zinc-800 border-zinc-700 h-9"
              />
            </div>

            {/* Artist Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Artist</Label>
              <Select value={selectedArtist} onValueChange={setSelectedArtist}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9">
                  <SelectValue placeholder="All Artists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Artists</SelectItem>
                  {data?.filters?.artists?.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Service Type Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Service Type</Label>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {data?.filters?.service_types?.map((st) => (
                    <SelectItem key={st} value={st}>{st}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={applyFilters} size="sm" className="h-9">
                Apply
              </Button>
              <Button onClick={resetFilters} variant="outline" size="sm" className="h-9">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Table - Artists by Months */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <Palette className="h-4 w-4 text-purple-500" />
            Payments by Artist
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {!data?.artists?.length ? (
            <div className="text-center py-12 text-zinc-500">
              <Palette className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No data for selected period</p>
            </div>
          ) : (
            <Table className="min-w-max">
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium sticky left-0 bg-zinc-900 z-10 min-w-[200px]">
                    Artist
                  </TableHead>
                  {data.months.map((month) => (
                    <TableHead key={month} className="text-zinc-400 font-medium text-center min-w-[120px]">
                      {formatMonthShort(month)}
                    </TableHead>
                  ))}
                  <TableHead className="text-zinc-400 font-medium text-right min-w-[100px] bg-zinc-800/50">
                    Total
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.artists.map((artist) => (
                  <React.Fragment key={artist.id}>
                    <TableRow 
                      className={cn(
                        "border-zinc-800 hover:bg-zinc-800/50 cursor-pointer",
                        expandedArtist === artist.id && "bg-zinc-800/30"
                      )}
                      onClick={() => handleExpandArtist(artist.id)}
                    >
                      <TableCell className="sticky left-0 bg-zinc-900 z-10">
                        <div className="flex items-center gap-2">
                          {expandedArtist === artist.id ? (
                            <ChevronUp className="h-4 w-4 text-zinc-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-zinc-500" />
                          )}
                          <span className="text-zinc-50 font-medium">{artist.name}</span>
                        </div>
                      </TableCell>
                      {data.months.map((month) => {
                        const monthData = artist.months[month] || { total: 0 };
                        return (
                          <TableCell key={month} className="text-center">
                            {monthData.total > 0 ? (
                              <span className="font-mono text-zinc-50">
                                {formatAmount(monthData.total, "UAH")}
                              </span>
                            ) : (
                              <span className="text-zinc-600">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right bg-zinc-800/30">
                        <span className="font-mono text-purple-400 font-bold">
                          {formatAmount(artist.totals.total, "UAH")}
                        </span>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Row - Status Breakdown */}
                    {expandedArtist === artist.id && (
                      <>
                        {/* Paid row */}
                        <TableRow className="border-zinc-800 bg-zinc-900/50">
                          <TableCell className="sticky left-0 bg-zinc-900/50 z-10 pl-10">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              <span className="text-xs text-green-400">Paid</span>
                            </div>
                          </TableCell>
                          {data.months.map((month) => {
                            const monthData = artist.months[month] || { paid: 0 };
                            return (
                              <TableCell key={month} className="text-center">
                                {monthData.paid > 0 ? (
                                  <span className="font-mono text-xs text-green-400">
                                    {formatAmount(monthData.paid, "UAH")}
                                  </span>
                                ) : (
                                  <span className="text-zinc-700">—</span>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right bg-zinc-800/20">
                            <span className="font-mono text-xs text-green-400">
                              {formatAmount(artist.totals.paid, "UAH")}
                            </span>
                          </TableCell>
                        </TableRow>
                        
                        {/* Approved row */}
                        <TableRow className="border-zinc-800 bg-zinc-900/50">
                          <TableCell className="sticky left-0 bg-zinc-900/50 z-10 pl-10">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-3 w-3 text-blue-500" />
                              <span className="text-xs text-blue-400">Approved</span>
                            </div>
                          </TableCell>
                          {data.months.map((month) => {
                            const monthData = artist.months[month] || { approved: 0 };
                            return (
                              <TableCell key={month} className="text-center">
                                {monthData.approved > 0 ? (
                                  <span className="font-mono text-xs text-blue-400">
                                    {formatAmount(monthData.approved, "UAH")}
                                  </span>
                                ) : (
                                  <span className="text-zinc-700">—</span>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right bg-zinc-800/20">
                            <span className="font-mono text-xs text-blue-400">
                              {formatAmount(artist.totals.approved, "UAH")}
                            </span>
                          </TableCell>
                        </TableRow>
                        
                        {/* Pending row */}
                        <TableRow className="border-zinc-800 bg-zinc-900/50">
                          <TableCell className="sticky left-0 bg-zinc-900/50 z-10 pl-10">
                            <div className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-orange-500" />
                              <span className="text-xs text-orange-400">Pending</span>
                            </div>
                          </TableCell>
                          {data.months.map((month) => {
                            const monthData = artist.months[month] || { pending: 0 };
                            return (
                              <TableCell key={month} className="text-center">
                                {monthData.pending > 0 ? (
                                  <span className="font-mono text-xs text-orange-400">
                                    {formatAmount(monthData.pending, "UAH")}
                                  </span>
                                ) : (
                                  <span className="text-zinc-700">—</span>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right bg-zinc-800/20">
                            <span className="font-mono text-xs text-orange-400">
                              {formatAmount(artist.totals.pending, "UAH")}
                            </span>
                          </TableCell>
                        </TableRow>
                        
                        {/* Details table */}
                        <TableRow className="border-zinc-800 bg-zinc-950">
                          <TableCell colSpan={data.months.length + 2} className="p-4">
                            {loadingDetails ? (
                              <div className="text-center text-zinc-500 py-4">Завантаження деталей...</div>
                            ) : artistDetails ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-medium text-zinc-50">Деталізація платежів</h4>
                                  <div className="text-xs text-zinc-500">
                                    Курс: $1 = ₴{artistDetails.exchange_rates?.usd?.toFixed(2)}
                                  </div>
                                </div>
                                <div className="max-h-[300px] overflow-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="border-zinc-800">
                                        <TableHead className="text-zinc-400 text-xs w-20">Місяць</TableHead>
                                        <TableHead className="text-zinc-400 text-xs">Проект / Таска</TableHead>
                                        <TableHead className="text-zinc-400 text-xs text-right w-24">Сума</TableHead>
                                        <TableHead className="text-zinc-400 text-xs text-right w-24">UAH</TableHead>
                                        <TableHead className="text-zinc-400 text-xs w-40">Статус / Дії</TableHead>
                                        <TableHead className="text-zinc-400 text-xs w-28">Дата оплати</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {artistDetails.details.map((detail, idx) => (
                                        <TableRow key={idx} className="border-zinc-800 hover:bg-zinc-800/30">
                                          <TableCell className="text-xs text-zinc-400">
                                            {detail.payment_month}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            <div className="text-zinc-50">{detail.task_name?.substring(0, 35)}{detail.task_name?.length > 35 ? '...' : ''}</div>
                                            <div className="text-zinc-500 text-[10px]">{detail.project_name}</div>
                                          </TableCell>
                                          <TableCell className="text-xs text-right font-mono">
                                            {detail.currency === 'UAH' ? '₴' : '$'}{detail.amount?.toLocaleString()}
                                          </TableCell>
                                          <TableCell className="text-xs text-right font-mono text-zinc-50">
                                            ₴{detail.amount_uah?.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-2">
                                              <Badge className={cn(
                                                "text-[10px] h-5",
                                                detail.status === 'paid' ? "bg-green-500/20 text-green-400 border-green-500/30" :
                                                detail.status === 'approved' ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                                                "bg-orange-500/20 text-orange-400 border-orange-500/30"
                                              )}>
                                                {detail.status === 'paid' ? 'Оплачено' : 
                                                 detail.status === 'approved' ? 'Підтв.' : 'Очікує'}
                                              </Badge>
                                              {detail.status === 'pending' && (
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePaymentStatusChange(detail, 'approved');
                                                  }}
                                                >
                                                  <CheckCircle className="h-3 w-3 mr-1" />
                                                  Підтв.
                                                </Button>
                                              )}
                                              {detail.status === 'approved' && (
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-6 px-2 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePaymentStatusChange(detail, 'paid');
                                                  }}
                                                >
                                                  <CreditCard className="h-3 w-3 mr-1" />
                                                  Оплатити
                                                </Button>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-xs text-zinc-400">
                                            {detail.status === 'paid' && detail.paid_date ? (
                                              <span className="text-green-400 font-mono">{detail.paid_date.substring(0, 10)}</span>
                                            ) : detail.status === 'approved' ? (
                                              <span className="text-blue-400/70 italic text-[10px]">очікує</span>
                                            ) : (
                                              <span className="text-zinc-600">—</span>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                                <div className="flex justify-end gap-4 pt-2 border-t border-zinc-800">
                                  <div className="text-xs">
                                    <span className="text-green-400">Оплачено: </span>
                                    <span className="font-mono text-zinc-50">₴{artistDetails.totals?.paid?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                  </div>
                                  <div className="text-xs">
                                    <span className="text-blue-400">Підтверджено: </span>
                                    <span className="font-mono text-zinc-50">₴{artistDetails.totals?.approved?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                  </div>
                                  <div className="text-xs">
                                    <span className="text-orange-400">Очікує: </span>
                                    <span className="font-mono text-zinc-50">₴{artistDetails.totals?.pending?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-zinc-500 py-4">Немає даних</div>
                            )}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </React.Fragment>
                ))}
                
                {/* Grand Totals Row */}
                <TableRow className="border-zinc-800 bg-zinc-800/50 font-bold">
                  <TableCell className="sticky left-0 bg-zinc-800/50 z-10">
                    <span className="text-zinc-50 font-bold">TOTAL</span>
                  </TableCell>
                  {data.months.map((month) => {
                    const monthTotal = data.grand_totals[month] || { total: 0 };
                    return (
                      <TableCell key={month} className="text-center">
                        <span className="font-mono text-zinc-50 font-bold">
                          {formatAmount(monthTotal.total, "UAH")}
                        </span>
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right bg-purple-900/20">
                    <span className="font-mono text-purple-400 font-bold">
                      {formatAmount(totals.total, "UAH")}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ArtistPnlPage;
